import RefreshIcon from '@mui/icons-material/Refresh';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from '@mui/material';
import {isAxiosError} from 'axios';
import {FC, FormEvent, useEffect, useRef, useState} from 'react';
import {UserErrorMessage} from '../../../global/api';
import {getDemoCaptcha} from '../api';
import {DemoCaptchaSolution} from '../types';

interface Props {
  onClose: () => void;
  onConfirm: (solution: DemoCaptchaSolution) => Promise<void>;
}

export const CaptchaDialog: FC<Props> = ({onClose, onConfirm}) => {
  const [captcha, setCaptcha] = useState<{id: string; imageUrl: string} | null>(null);
  const [answer, setAnswer] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const submittingRef = useRef(false);

  useEffect(() => {
    const controller = new AbortController();
    let imageUrl: string | undefined;

    const loadCaptcha = async () => {
      try {
        const {id, image} = await getDemoCaptcha(controller.signal);
        if (controller.signal.aborted) return;

        imageUrl = URL.createObjectURL(image);
        setCaptcha({id, imageUrl});
      } catch {
        if (!controller.signal.aborted) {
          setError('Не удалось загрузить капчу. Попробуйте обновить картинку.');
        }
      } finally {
        if (!controller.signal.aborted) setIsLoading(false);
      }
    };

    void loadCaptcha();

    return () => {
      controller.abort();
      if (imageUrl) URL.revokeObjectURL(imageUrl);
    };
  }, [refreshKey]);

  const refreshCaptcha = () => {
    setCaptcha(null);
    setAnswer('');
    setIsImageLoaded(false);
    setIsLoading(true);
    setRefreshKey((value) => value + 1);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submittingRef.current || isLoading || !isImageLoaded || !captcha || !answer.trim()) return;

    submittingRef.current = true;
    setIsSubmitting(true);
    setError('');

    try {
      await onConfirm({captchaId: captcha.id, captcha: answer.trim()});
    } catch (error) {
      const data = isAxiosError<UserErrorMessage>(error) ? error.response?.data : undefined;
      setError(data?.message || data?.title || 'Не удалось отправить заявку. Попробуйте ещё раз.');
      // The API has no separate captcha error code; renew it after every failed attempt.
      refreshCaptcha();
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open
      fullWidth
      maxWidth="xs"
      onClose={() => {
        if (!submittingRef.current) onClose();
      }}
      disableEscapeKeyDown={isSubmitting}
      aria-labelledby="demo-captcha-title"
      aria-describedby="demo-captcha-description"
      PaperProps={{component: 'form', onSubmit: handleSubmit}}
    >
      <DialogTitle id="demo-captcha-title">Подтвердите, что вы не робот</DialogTitle>
      <DialogContent>
        <DialogContentText id="demo-captcha-description">
          Введите символы с картинки, чтобы отправить заявку на демо-доступ.
        </DialogContentText>
        {error && (
          <Alert severity="error" sx={{mt: 2}}>
            {error}
          </Alert>
        )}
        <Box sx={{minHeight: 120, my: 2, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
          {(isLoading || (captcha && !isImageLoaded)) && <CircularProgress aria-label="Загрузка капчи" />}
          {captcha && (
            <Box
              component="img"
              src={captcha.imageUrl}
              alt="Капча с символами для подтверждения регистрации"
              sx={{display: isImageLoaded ? 'block' : 'none', maxWidth: '100%'}}
              onLoad={() => setIsImageLoaded(true)}
              onError={() => {
                setCaptcha(null);
                setIsImageLoaded(false);
                setError('Не удалось отобразить капчу. Попробуйте обновить картинку.');
              }}
            />
          )}
        </Box>
        <Button
          type="button"
          startIcon={<RefreshIcon />}
          disabled={isLoading || isSubmitting}
          onClick={() => {
            setError('');
            refreshCaptcha();
          }}
          sx={{mb: 2}}
        >
          Обновить картинку
        </Button>
        <TextField
          autoFocus
          fullWidth
          required
          id="demo-captcha-answer"
          label="Символы с картинки"
          value={answer}
          onChange={(event) => setAnswer(event.target.value)}
          disabled={isSubmitting}
          autoComplete="off"
          inputProps={{autoCapitalize: 'none', spellCheck: false}}
        />
      </DialogContent>
      <DialogActions sx={{px: 3, pb: 3}}>
        <Button type="button" onClick={onClose} disabled={isSubmitting}>
          Отмена
        </Button>
        <LoadingButton
          type="submit"
          variant="contained"
          loading={isSubmitting}
          disabled={isLoading || !isImageLoaded || !captcha || !answer.trim()}
        >
          Отправить заявку
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
